/**
 * Convites — regras puras.
 *
 * Duas vias com níveis de segurança diferentes, e é por isso que existem
 * duas colunas no banco:
 *
 * | Via    | Entropia                        | Defesa                     |
 * |--------|---------------------------------|----------------------------|
 * | Link   | token de 32 bytes, só o hash    | comparação de hash         |
 * | Código | 8 caracteres do alfabeto seguro | entropia (limite folgado)  |
 *
 * O alfabeto do código exclui `0/O` e `1/I/L`: o código é lido em voz alta e
 * digitado à mão, e um caractere ambíguo vira um pedido de ajuda ao grupo.
 */

/** Sem 0, O, 1, I, L — ambíguos quando lidos ou digitados. */
export const INVITE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
/**
 * 8 caracteres num alfabeto de 31: ~8,5 × 10¹¹ combinações. Com 6 o código
 * só era aceitável por causa de um limite apertado de tentativas — e esse
 * limite bloqueava famílias inteiras atrás do mesmo IP. Com 8, o limite
 * pode ser folgado sem que adivinhar seja viável.
 */
export const INVITE_CODE_LENGTH = 8;
export const INVITE_PREFIX = 'NV-';

export type InvitationStatus = 'pending' | 'opened' | 'accepted' | 'revoked' | 'expired';

export interface Invitation {
  status: InvitationStatus;
  /** Instante de expiração em ms. */
  expiresAt: number;
  acceptedBy?: string | null;
}

export type InvitationRejection =
  | 'not_found'
  | 'expired'
  | 'revoked'
  | 'already_used';

export type InvitationCheck =
  | { valid: true }
  | { valid: false; reason: InvitationRejection };

/**
 * O convite pode ser usado agora?
 *
 * A ordem das checagens importa para a mensagem: "expirado" é mais útil que
 * "já usado" quando as duas coisas são verdade, porque a pessoa consegue
 * pedir um convite novo.
 */
export function checkInvitation(
  invitation: Invitation | null,
  now: number = Date.now(),
): InvitationCheck {
  if (!invitation) return { valid: false, reason: 'not_found' };
  if (invitation.status === 'revoked') return { valid: false, reason: 'revoked' };
  if (invitation.expiresAt <= now) return { valid: false, reason: 'expired' };
  // Uso único: aceito uma vez, não serve de novo nem para a mesma pessoa.
  if (invitation.status === 'accepted') return { valid: false, reason: 'already_used' };
  return { valid: true };
}

export const INVITATION_MESSAGES: Record<InvitationRejection, string> = {
  not_found: 'Este convite não existe. Confira o código ou peça um link novo.',
  expired: 'Este convite expirou. Peça um novo a quem te convidou.',
  revoked: 'Este convite foi cancelado. Peça um novo a quem te convidou.',
  already_used: 'Este convite já foi usado. Peça um novo a quem te convidou.',
};

/** Formata para exibição em dois blocos: `A7K2-M9XQ` — mais fácil de ditar. */
export function formatInviteCode(code: string): string {
  const c = code.toUpperCase();
  return c.length === 8 ? `${c.slice(0, 4)}-${c.slice(4)}` : `${INVITE_PREFIX}${c}`;
}

/**
 * Normaliza o que a pessoa digitou.
 *
 * Aceita com e sem prefixo, com espaços, em minúsculas — e converte os
 * ambíguos mais comuns, porque quem leu `NV-O0I1` de uma tela vai digitar o
 * que enxergou, não o que estava lá.
 */
export function normalizeInviteCode(input: string): string | null {
  const semEspacos = input
    .trim()
    .toUpperCase()
    .replace(/\s|-/g, '');
  // O prefixo de marca ("NV-XXXXXXXX", "VJ-XXXXXXXX") só sai quando é
  // prefixo mesmo: um código de 8 letras que COMEÇA com "NV" ou "VJ" é um
  // código válido (N, V e J estão no alfabeto), e tirar as duas letras dele
  // o deixava com 6 e o derrubava — um em cada ~480 convites (flake no CI).
  const semPrefixo = semEspacos.length > INVITE_CODE_LENGTH ? semEspacos.replace(/^(NV|VJ)/, '') : semEspacos;
  const limpo = semPrefixo
    .replace(/[O]/g, '0')
    .replace(/[IL]/g, '1');

  // Depois de desambiguar para 0 e 1, volta para os caracteres do alfabeto.
  const corrigido = limpo.replace(/0/g, 'O').replace(/1/g, 'I');

  const candidato = [...corrigido]
    .map((c) => {
      if (c === 'O') return null;
      if (c === 'I') return null;
      return c;
    })
    .join('');

  if (candidato.length !== INVITE_CODE_LENGTH) return null;
  if (![...candidato].every((c) => INVITE_ALPHABET.includes(c))) return null;
  return candidato;
}

/** Gera um código a partir de bytes aleatórios, sem viés de módulo. */
export function generateInviteCode(random: Uint8Array): string {
  const limite = Math.floor(256 / INVITE_ALPHABET.length) * INVITE_ALPHABET.length;
  const saida: string[] = [];

  for (const byte of random) {
    // Descarta bytes acima do limite: usar módulo direto tornaria os
    // primeiros caracteres do alfabeto mais prováveis.
    if (byte >= limite) continue;
    saida.push(INVITE_ALPHABET[byte % INVITE_ALPHABET.length] as string);
    if (saida.length === INVITE_CODE_LENGTH) break;
  }

  if (saida.length < INVITE_CODE_LENGTH) {
    throw new Error('Bytes aleatórios insuficientes para gerar o código.');
  }
  return saida.join('');
}

/** Espaço de busca do código, para justificar o rate limit. */
export const INVITE_CODE_SPACE = INVITE_ALPHABET.length ** INVITE_CODE_LENGTH;
